import type {
  IRegistrationCrmConfig,
  IRegistrationFormConfig,
  IRegistrationHealthConfig,
  IRegistrationPage,
  IRegistrationSlugCheck,
} from './registration-page.model';

/**
 * Contracts of the registration Worker's link endpoints (`/api/pages*`).
 *
 * A gym may have several registration links, for different purposes. Each one
 * is a page with its own address, settings and registrations, plus a label the
 * staff tell them apart by.
 */

/** How many registrations a link holds, by where each one is. */
export interface IRegistrationLinkCounts {
  /** Waiting to be handled at the counter. */
  pending: number;
  /** Handled. They still hold their CPF on the link until it is emptied. */
  imported: number;
  /** In the trash, until restored or purged. */
  trashed: number;
}

/**
 * How the visitor fills the registration in on a link: the form in steps, or
 * the conversation that asks one field at a time. Both ask the same fields and
 * send the same registration; only the screen changes.
 */
export type TRegistrationExperience = 'form' | 'chat';

/**
 * The image at the top of a link's public page, next to its display name: the
 * Actuar symbol, the gym's square or horizontal logo from Settings › Logos, or
 * an image uploaded for this link alone.
 */
export type TRegistrationLogo = 'actuar' | 'square' | 'rectangular' | 'custom';

/** What saving a link's logo sends. `url` goes only with `custom`: the other three are not images of the link. */
export interface IRegistrationLogoPayload {
  logo: TRegistrationLogo;
  url?: string;
}

export interface IRegistrationLink extends IRegistrationPage {
  /**
   * The staff's label for the link. `''` is a link nobody named yet: the one a
   * gym already had before it could have several. The visitor never reads it.
   */
  name: string;
  counts: IRegistrationLinkCounts;
  /**
   * Every `/api/pages*` answer carries the three. They are optional only on
   * the page of a Worker older than each setting, and that Worker has no list
   * of links.
   */
  form: IRegistrationFormConfig;
  crm: IRegistrationCrmConfig;
  health: IRegistrationHealthConfig;
  /**
   * What the link opens for its visitor. A link is born with the form, and
   * only the staff switch it to the conversation. It is a setting of the links
   * alone: the routes without an id do not know it.
   */
  experience: TRegistrationExperience;
  /**
   * The image at the top of the link's public page. Every link is born with
   * the Actuar symbol, and only the staff switch it. The square and the
   * horizontal logo are the gym's, so changing them in Settings › Logos changes
   * them here too.
   */
  logo: TRegistrationLogo;
  /** The image uploaded for this link. Only `custom` has one: `null` for the other three. */
  logoUrl: string | null;
}

/** What creating a link, or renaming it and changing its address, sends. */
export interface IRegistrationLinkPayload {
  name: string;
  slug: string;
  displayName: string;
}

/** Answer of emptying a link: how many were deleted for good and how many went to the trash. */
export interface IRegistrationLinkEmptied {
  deleted: number;
  trashed: number;
}

/**
 * Answer of the address question, tied to the address and to the link it was
 * asked for. `linkId` is `null` for a link not created yet.
 */
export interface IRegistrationLinkSlugCheck extends IRegistrationSlugCheck {
  linkId: string | null;
}

/** Longest label the Worker takes for a link. */
export const REGISTRATION_LINK_NAME_MAX_LENGTH = 60;

/**
 * How many links a gym may have. Paused links count, and no route deletes one.
 * Mirrors `MAX_LINKS_PER_TENANT` of the Worker.
 */
export const REGISTRATION_LINK_MAX_COUNT = 20;
